import {ISearchableFields} from '../../shared-models/type-sense/default-searchable-fields';
import {TypeSenseFacetCounts, TypeSenseSearchResponse} from '../../shared-models/type-sense/type-sense-types';

/** Filter-map key for the stock refinement that excludes items tagged `disabled`. */
export const STOCK_REFINEMENT_HIDE_DISABLED_FIELD = '__hideDisabled';

const REFINEMENT_FACET_FIELDS = new Set(['3', '4', '40', '46', '_tags', 'multipleSuppliers']);

/**
 * Facet fields that currently have one or more selected values.
 * The hide-disabled toggle is not a facet, so it is excluded.
 */
export function activeRefinementFacetFields(
  filters?: Record<string, ISearchableFields>,
): string[] {
  if (!filters) {
    return [];
  }
  return Object.keys(filters).filter((field: string) =>
    REFINEMENT_FACET_FIELDS.has(field) && Object.keys(filters[field] ?? {}).length > 0,
  );
}

/**
 * True when the refinement map asks to hide items that carry the disabled tag.
 */
export function isHideDisabledRefinement(filters?: Record<string, ISearchableFields> | null): boolean {
  const entry = filters?.[STOCK_REFINEMENT_HIDE_DISABLED_FIELD];
  if (!entry) {
    return false;
  }
  return Object.values(entry).some((field) => field?.value === true);
}

/**
 * Builds a Typesense `filter_by` string from stock refinement selections.
 * Values inside one field are OR. Different fields are AND.
 * Pass `omitField` to leave one facet out so its option list can stay open for further selections.
 */
export function searchStockItemsCreateFilterString(
  obj: Record<string, ISearchableFields>,
  omitField?: string,
): string {
  const parts: string[] = [];
  if (isHideDisabledRefinement(obj)) {
    parts.push('_tags:!=disabled');
  }
  Object.entries(obj).forEach(([field, values]) => {
    if (field === STOCK_REFINEMENT_HIDE_DISABLED_FIELD || field === omitField) {
      return;
    }
    const keys = Object.keys(values ?? {});
    if (keys.length === 0) {
      return;
    }
    if (keys.length === 1) {
      parts.push(`${field}:=${formatRefinementFilterValue(keys[0])}`);
      return;
    }
    parts.push(`${field}:=[${keys.map((key: string) => formatRefinementFilterValue(key)).join(',')}]`);
  });
  return parts.join(' && ');
}

/**
 * Replaces facet counts for fields the user is multi-selecting with counts
 * computed without that field's own filter, so the remaining options stay visible.
 */
export function mergeDisjunctiveFacetCounts<T>(
  main: TypeSenseSearchResponse<T>,
  extras: (TypeSenseSearchResponse<T> | undefined)[],
  fields: string[],
): TypeSenseSearchResponse<T> {
  const facetCounts: TypeSenseFacetCounts[] = [...(main.facet_counts ?? [])];
  fields.forEach((field: string, index: number) => {
    const extra = extras[index];
    if (!extra?.facet_counts) {
      return;
    }
    const replacement = extra.facet_counts.find((facet: TypeSenseFacetCounts) => facet.field_name === field);
    if (!replacement) {
      return;
    }
    const existingIndex = facetCounts.findIndex((facet: TypeSenseFacetCounts) => facet.field_name === field);
    if (existingIndex >= 0) {
      facetCounts[existingIndex] = replacement;
    } else {
      facetCounts.push(replacement);
    }
  });
  return {...main, facet_counts: facetCounts};
}

/**
 * Leaves plain codes unquoted. Wraps anything else in backticks so spaces and commas stay one value.
 */
function formatRefinementFilterValue(value: string): string {
  if (/^[A-Za-z0-9_-]+$/.test(value)) {
    return value;
  }
  const escaped = value.replace(/\\/g, '\\\\').replace(/`/g, '\\`');
  return `\`${escaped}\``;
}
